import { Link } from "@tanstack/react-router";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "./Logo";
import { useCart } from "@/hooks/useCart";
import { cartItemCount } from "@/lib/cart";
import { navQueryOptions } from "@/lib/navigation.functions";
import { DEFAULT_HEADER_NAV, isExternalHref, type NavItem } from "@/lib/navigation";

type SimpleLink = { key: string; label: string; href: string; openInNew: boolean };

const FALLBACK: SimpleLink[] = DEFAULT_HEADER_NAV.map((n) => ({
  key: n.href,
  label: n.label,
  href: n.href,
  openInNew: false,
}));

export function useHeaderNav(): SimpleLink[] {
  const { data } = useQuery(navQueryOptions());
  const items = (data ?? []).filter((i: NavItem) => i.area === "header");
  if (!items.length) return FALLBACK;
  return items.map((i) => ({ key: i.id, label: i.label, href: i.href, openInNew: i.open_in_new }));
}

/** Renders a router Link for internal paths and a plain anchor for external URLs. */
export function NavAnchor({
  href,
  openInNew,
  className,
  activeClassName,
  onClick,
  children,
}: {
  href: string;
  openInNew?: boolean;
  className?: string;
  activeClassName?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  if (isExternalHref(href) || openInNew) {
    return (
      <a
        href={href}
        className={className}
        onClick={onClick}
        {...(openInNew || isExternalHref(href)
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={href}
      className={className}
      onClick={onClick}
      activeProps={activeClassName ? { className: activeClassName } : undefined}
    >
      {children}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const cart = useCart();
  const count = cartItemCount(cart);
  const nav = useHeaderNav();

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" aria-label="Glow Long Golf home">
          <Logo className="h-10 w-10" />
          <span className="font-display text-lg font-semibold tracking-tight">
            Glow <span className="text-primary text-glow">Long</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {nav.map((n) => (
            <NavAnchor
              key={n.key}
              href={n.href}
              openInNew={n.openInNew}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground transition hover:text-foreground"
              activeClassName="text-foreground"
            >
              {n.label}
            </NavAnchor>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/account"
            className="hidden h-10 items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-medium hover:bg-secondary sm:inline-flex"
          >
            Account
          </Link>
          <Link
            to="/cart"
            className="relative inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-medium hover:bg-secondary"
            aria-label={`Cart (${count} items)`}
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                {count}
              </span>
            )}
          </Link>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/60 bg-background md:hidden" aria-label="Mobile">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4">
            {nav.map((n) => (
              <NavAnchor
                key={n.key}
                href={n.href}
                openInNew={n.openInNew}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                activeClassName="text-foreground bg-secondary"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </NavAnchor>
            ))}
            <Link
              to="/account"
              className="mt-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary"
              onClick={() => setOpen(false)}
              activeProps={{ className: "bg-secondary" }}
            >
              Account
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
